import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Link, useInRouterContext, useLocation } from 'react-router-dom'
import { errorText, pageNameFor, stampDate } from './routeError'

/* Route error boundary (B6 audit blocker, 2026-09-04).
 *
 * Before this, `src/frontend/src` had NO error boundary: one API row missing
 * an array field (`fund_names` on a documents row — atlas shot 115,
 * "reading 'join'") unmounted the whole tree, rail included, and the
 * officer stared at a blank page with no door out.
 *
 * Two placements, one component:
 *   - INSIDE the v2 shell, around the routed page (<Outlet/>) — the rail
 *     stays usable, the page shows the designed fallback below. Keyed on
 *     location.pathname by the shell, so leaving the page resets it.
 *   - around <App/> in main.tsx as the last resort (router/auth failures).
 *
 * The fallback is a designed state in the canon: no red, no stack trace
 * for the officer — a plain sentence, the page's name and the time, two
 * doors (reload / back to Today), and a collapsed details line carrying
 * the error message for support. The stack goes to the console only. */

type Variant = 'page' | 'panel'

interface Props {
  children: ReactNode
  /** Page name the fallback states; defaults to the rail label of the
   * current pathname (via the router) or "this page" outside a router. */
  page?: string
  /** 'page' (default) fills the outlet slot; 'panel' is the compact form
   * for a side panel such as EILE, so a panel failure never reads as a
   * page failure. */
  variant?: Variant
}

interface State {
  error: unknown
  at: Date | null
}

export default class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, at: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error, at: new Date() }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // The one place the stack is kept: the console, for support. Never
    // the officer's screen.
    console.error(
      `[route-error] ${this.props.page ?? window.location.pathname} could not be drawn:`,
      error,
      info.componentStack ?? ''
    )
  }

  reset = () => this.setState({ error: null, at: null })

  render() {
    if (this.state.error === null) return this.props.children
    return (
      <RouteErrorFallback
        error={this.state.error}
        at={this.state.at ?? new Date()}
        page={this.props.page}
        variant={this.props.variant ?? 'page'}
        onReset={this.reset}
      />
    )
  }
}

interface FallbackProps {
  error: unknown
  at: Date
  page?: string
  variant: Variant
  onReset: () => void
}

/* The fallback is a function component so it can ask the router (when there
 * is one) where the officer is; the last-resort boundary in main.tsx sits
 * outside the BrowserRouter, so it falls back to a plain anchor. */
function RouteErrorFallback(props: FallbackProps) {
  const inRouter = useInRouterContext()
  return inRouter ? <RoutedFallback {...props} /> : <PlainFallback {...props} pathname={null} />
}

function RoutedFallback(props: FallbackProps) {
  const location = useLocation()
  return <PlainFallback {...props} pathname={location.pathname} />
}

function PlainFallback({
  error,
  at,
  page,
  variant,
  onReset,
  pathname,
}: FallbackProps & { pathname: string | null }) {
  const name = page ?? pageNameFor(pathname ?? window.location.pathname)
  const noun = variant === 'panel' ? 'panel' : 'page'
  const onToday = pathname === '/v2'
  const todayLabel = 'Back to Today'
  const today =
    pathname !== null ? (
      // Already on Today: the key does not change, so the boundary is
      // reset by hand instead — a retry of the same draw.
      <Link
        to="/v2"
        className="vbtn"
        data-testid="route-error-today"
        onClick={onToday ? onReset : undefined}
      >
        {todayLabel}
      </Link>
    ) : (
      <a href="/v2" className="vbtn" data-testid="route-error-today">
        {todayLabel}
      </a>
    )
  const card = (
    <div className="vcard route-error-card" role="alert" data-testid="route-error">
      <p className="route-error-lead">This {noun} could not be drawn.</p>
      <p className="route-error-where">
        {name} · {stampDate(at)}
      </p>
      <div className="route-error-doors">
        <button
          type="button"
          className="vbtn pri"
          data-testid="route-error-reload"
          onClick={() => window.location.reload()}
        >
          Reload this {noun}
        </button>
        {today}
      </div>
      <details className="route-error-details" data-testid="route-error-details">
        <summary>Details for support</summary>
        <p className="mono">{errorText(error)}</p>
      </details>
    </div>
  )
  if (variant === 'panel') return <div className="route-error-panel">{card}</div>
  const slot = (
    <main className="detail route-error-page">
      <div className="route-error-slot">{card}</div>
    </main>
  )
  // Inside the shell the slot sits where the page would (a .v2 ancestor
  // scopes it). Outside any router — main.tsx's last resort — there is no
  // shell above, so the wrapper carries the .v2 scope: same page ground,
  // same card, same doors.
  return pathname === null ? <div className="v2">{slot}</div> : slot
}
